import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mountain, Users, Home, Leaf, Zap, Sparkles,
  ChefHat, Sofa, Palette, SlidersHorizontal,
  ChevronDown, ChevronUp, CheckCircle, RefreshCw, X,
} from 'lucide-react';
import {
  AMENITY_SECTIONS, STYLE_OPTIONS, KITCHEN_SECTIONS,
  FURNISHING_STATUS, FURNISHING_INCLUDES,
  EMPTY_AMENITIES, EMPTY_KITCHEN, EMPTY_FURNISHING,
} from '../utils/canonical';

const API    = process.env.REACT_APP_BACKEND_URL;
const ACCENT = '#0A192F';
const GREEN  = '#10B981';

// ─── Section icon map ─────────────────────────────────────────────────────────
const ICON_MAP = { Mountain, Users, Home, Leaf, Zap, Sparkles };

// ─── Pill chip ────────────────────────────────────────────────────────────────
function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150 select-none"
      style={active
        ? { background: ACCENT, color: 'white', borderColor: ACCENT }
        : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
      {active && <CheckCircle size={9} />}
      {children}
    </button>
  );
}

// ─── Accordion section ────────────────────────────────────────────────────────
function Section({ title, iconName, children, count = 0, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = ICON_MAP[iconName] || SlidersHorizontal;
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left">
        <div className="flex items-center gap-2">
          <Icon size={14} style={{ color: ACCENT }} />
          <span className="text-sm font-bold" style={{ color: ACCENT }}>{title}</span>
          {count > 0 && (
            <span className="text-xs font-black px-2 py-0.5 rounded-full text-white" style={{ background: GREEN }}>
              {count}
            </span>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
            className="overflow-hidden border-t border-slate-100">
            <div className="px-4 py-3 bg-slate-50">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main RefinementPanel ─────────────────────────────────────────────────────
export default function RefinementPanel({ onApply, defaultOpen = false }) {
  const { authFetch } = useAuth();
  const [open, setOpen] = useState(defaultOpen);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ── local state mirrors canonical schema ──
  const [amenities,  setAmenities]  = useState(EMPTY_AMENITIES);
  const [style,      setStyle]      = useState([]);
  const [kitchen,    setKitchen]    = useState(EMPTY_KITCHEN);
  const [furnishing, setFurnishing] = useState(EMPTY_FURNISHING);

  // Load existing prefs from profile
  useEffect(() => {
    authFetch(`${API}/api/buyer/profile`)
      .then(r => r.ok ? r.json() : {})
      .then(p => {
        if (p.amenities)  setAmenities({ ...EMPTY_AMENITIES, ...p.amenities });
        if (p.style)      setStyle(p.style || []);
        if (p.kitchen)    setKitchen({ ...EMPTY_KITCHEN, ...p.kitchen });
        if (p.furnishing) setFurnishing({ ...EMPTY_FURNISHING, ...p.furnishing });
      })
      .catch(() => {});
  }, [authFetch]);

  // ── toggle helpers ──
  const toggleAmenity = useCallback((cat, id) => {
    setAmenities(prev => {
      const arr = prev[cat] || [];
      return { ...prev, [cat]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
    });
  }, []);

  const toggleStyle = useCallback(id => {
    setStyle(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const toggleKitchen = useCallback((cat, id) => {
    setKitchen(prev => {
      const arr = prev[cat] || [];
      return { ...prev, [cat]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
    });
  }, []);

  const toggleFurnInclude = useCallback(id => {
    setFurnishing(prev => {
      const arr = prev.includes || [];
      return { ...prev, includes: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
    });
  }, []);

  // ── count helpers ──
  const amenityCount = Object.values(amenities).flat().length;
  const kitchenCount = Object.values(kitchen).flat().length;
  const totalSelections = amenityCount + style.length + kitchenCount +
    (furnishing.status ? 1 : 0) + (furnishing.includes?.length || 0);

  const handleApply = async () => {
    setSaving(true);
    try {
      await authFetch(`${API}/api/buyer/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amenities, style, kitchen, furnishing }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onApply) onApply();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleReset = () => {
    setAmenities(EMPTY_AMENITIES);
    setStyle([]);
    setKitchen(EMPTY_KITCHEN);
    setFurnishing(EMPTY_FURNISHING);
  };

  return (
    <div className="mb-6" data-testid="refinement-panel">
      {/* Toggle bar */}
      <button onClick={() => setOpen(o => !o)}
        data-testid="refinement-toggle"
        className="w-full flex items-center justify-between px-5 py-3.5 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all group">
        <div className="flex items-center gap-2.5">
          <SlidersHorizontal size={16} style={{ color: ACCENT }} />
          <span className="font-bold text-sm" style={{ color: ACCENT }}>Tell us what you're into</span>
          {totalSelections > 0 && (
            <span className="text-xs font-black px-2.5 py-0.5 rounded-full text-white" style={{ background: GREEN }}>
              {totalSelections} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {totalSelections > 0 && (
            <button onClick={e => { e.stopPropagation(); handleReset(); }}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1">
              <X size={11} />Clear
            </button>
          )}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="mt-2 p-4 rounded-2xl border border-slate-200 bg-white shadow-sm space-y-3">

              {/* ── Amenity sections (6 collapsed) ── */}
              {AMENITY_SECTIONS.map(sec => {
                const count = (amenities[sec.key] || []).length;
                return (
                  <Section key={sec.key} title={sec.label} iconName={sec.icon} count={count}>
                    <div className="flex flex-wrap gap-1.5">
                      {sec.items.map(item => (
                        <Chip key={item.id}
                          active={(amenities[sec.key] || []).includes(item.id)}
                          onClick={() => toggleAmenity(sec.key, item.id)}>
                          {item.label}
                        </Chip>
                      ))}
                    </div>
                  </Section>
                );
              })}

              {/* ── Style ── */}
              <Section title="What kind of home makes you feel something?" iconName="Palette" count={style.length}>
                <div className="flex flex-wrap gap-1.5">
                  {STYLE_OPTIONS.map(s => (
                    <Chip key={s.id} active={style.includes(s.id)} onClick={() => toggleStyle(s.id)}>
                      {s.label}
                    </Chip>
                  ))}
                </div>
              </Section>

              {/* ── Kitchen ── */}
              <Section title="Let's talk kitchen (be honest)" iconName="ChefHat" count={kitchenCount}>
                <div className="space-y-3">
                  {KITCHEN_SECTIONS.map(sec => (
                    <div key={sec.key}>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">{sec.label}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {sec.items.map(item => (
                          <Chip key={item.id}
                            active={(kitchen[sec.key] || []).includes(item.id)}
                            onClick={() => toggleKitchen(sec.key, item.id)}>
                            {item.label}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* ── Furnishing / Move-In Ready ── */}
              <Section title="Move-In Ready" iconName="Sofa"
                count={(furnishing.status ? 1 : 0) + (furnishing.includes?.length || 0)}>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Furnishing</p>
                    <div className="flex flex-wrap gap-1.5">
                      {FURNISHING_STATUS.map(s => (
                        <Chip key={s.id}
                          active={furnishing.status === s.id}
                          onClick={() => setFurnishing(prev => ({
                            ...prev, status: prev.status === s.id ? '' : s.id,
                          }))}>
                          {s.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Includes</p>
                    <div className="flex flex-wrap gap-1.5">
                      {FURNISHING_INCLUDES.map(i => (
                        <Chip key={i.id}
                          active={(furnishing.includes || []).includes(i.id)}
                          onClick={() => toggleFurnInclude(i.id)}>
                          {i.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                </div>
              </Section>

              {/* ── Apply + Reset ── */}
              <div className="flex gap-3 pt-2">
                <button onClick={handleApply} disabled={saving}
                  data-testid="refinement-apply-btn"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: saved ? GREEN : ACCENT }}>
                  {saving ? <RefreshCw size={14} className="animate-spin" /> :
                    saved ? <><CheckCircle size={14} />Applied!</> :
                    <><SlidersHorizontal size={14} />Apply Refinements</>}
                </button>
                {totalSelections > 0 && (
                  <button onClick={handleReset}
                    className="px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                    Reset
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
